import * as styles from "./ImageCropper.css";

import { useCallback, useState } from "react";
import type { JSX } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";

import { Button } from "@frontend/components/atoms/Button";
import { DialogFooter } from "@frontend/components/atoms/Dialog";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  Slider,
  SliderControl,
  SliderIndicator,
  SliderThumb,
  SliderTrack,
} from "@frontend/components/atoms/Slider";
import { useLingui } from "@lingui/react/macro";

import type { ImageCropperProps } from "./ImageCropper.types";
import { ImageEditorBody } from "./ImageEditorBody";
import { getCroppedImage } from "./cropUtils";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const INITIAL_CROP = { x: 0, y: 0 };

export function ImageCropper({
  imageSrc,
  aspect = 1,
  onConfirm,
}: ImageCropperProps): JSX.Element {
  const { t } = useLingui();

  const [crop, setCrop] = useState(INITIAL_CROP);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const pristine = zoom === MIN_ZOOM && crop.x === 0 && crop.y === 0;
  const round = aspect === 1;

  const handleReset = () => {
    setCrop(INITIAL_CROP);
    setZoom(MIN_ZOOM);
  };

  const handleConfirm = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    onConfirm(await getCroppedImage(imageSrc, croppedAreaPixels));
  };

  return (
    <>
      <ImageEditorBody>
        <Cropper
          classes={{
            containerClassName: styles.cropper,
            mediaClassName: styles.media,
            cropAreaClassName: styles.cropArea({ round }),
          }}
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          aspect={aspect}
          cropShape={round ? "round" : "rect"}
          showGrid={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </ImageEditorBody>

      <DialogFooter>
        <div className={styles.zoom}>
          <Slider
            aria-label={t`Zoom`}
            value={zoom}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            format={{ style: "percent", maximumFractionDigits: 0 }}
            onValueChange={(value) => setZoom(value)}
          >
            <SliderControl>
              <SliderTrack>
                <SliderIndicator />
                <SliderThumb tooltip="auto" />
              </SliderTrack>
            </SliderControl>
          </Slider>
        </div>

        <Button
          variant="secondary"
          aria-label={t`Reset`}
          disabled={pristine}
          onClick={handleReset}
        >
          <Icon name="RotateCcw" />
        </Button>
        <Button label={t`Confirm`} onClick={handleConfirm} />
      </DialogFooter>
    </>
  );
}
